import {useEffect, useRef, type RefObject} from 'react';
import {useFormContext} from 'react-hook-form';
import type {DatepickerHandle} from 'components/common/Datepicker';
import {FIELD_TYPES} from '@chekin/core';
import {useLayoutMode} from 'hooks/useLayoutMode';
import {useVirtualKeyboard} from './useVirtualKeyboard';

export const useCustomFieldsVirtualKeyboard = (
  fieldName: string,
  fieldType: FIELD_TYPES,
  datePickerRef?: RefObject<DatepickerHandle>,
) => {
  const {getValues, setValue} = useFormContext();
  const {registerCustomField, handleFocus, handleBlur} = useVirtualKeyboard();
  const layoutMode = useLayoutMode();

  const getValuesRef = useRef(getValues);
  const setValueRef = useRef(setValue);

  useEffect(() => {
    getValuesRef.current = getValues;
    setValueRef.current = setValue;
  }, [getValues, setValue]);

  useEffect(() => {
    if (layoutMode !== 'kiosko' || !registerCustomField) {
      return;
    }

    let cleanup: (() => void) | undefined;

    if (fieldType === FIELD_TYPES.text) {
      cleanup = registerCustomField(fieldName, {
        getValue: () => getValuesRef.current(fieldName) || '',
        setValue: (value: string) => setValueRef.current(fieldName, value),
      });
    } else if (fieldType === FIELD_TYPES.date && datePickerRef) {
      const cleanupDay = registerCustomField(`${fieldName}_day`, {
        getValue: () => datePickerRef.current?.getDayValue() || '',
        setValue: (value: string) => datePickerRef.current?.setDayValue(value),
      });

      const cleanupYear = registerCustomField(`${fieldName}_year`, {
        getValue: () => datePickerRef.current?.getYearValue() || '',
        setValue: (value: string) => datePickerRef.current?.setYearValue(value),
      });

      cleanup = () => {
        cleanupDay();
        cleanupYear();
      };
    }

    return cleanup;
  }, [fieldName, fieldType, registerCustomField, layoutMode, datePickerRef]);

  return {
    onFocus: () => handleFocus(fieldName),
    onBlur: () => handleBlur(fieldName),
    onFieldFocus: (subfield: 'day' | 'year') => handleFocus(`${fieldName}_${subfield}`),
    onFieldBlur: (subfield: 'day' | 'year') => handleBlur(`${fieldName}_${subfield}`),
  };
};
